import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dices, Gamepad2, Monitor } from 'lucide-react';
import type { ReactNode } from 'react';
import type { GameRecord } from '@pixel-archive/shared';
import { getRediscoverCollection } from './supabase';
import {
  drawGame,
  eligibleGames,
  type RediscoverCollection,
  type RediscoverGame,
} from './rediscover';

export function RediscoverPage({ renderCover }: { renderCover: (game: GameRecord) => ReactNode }) {
  const [collection, setCollection] = useState<RediscoverCollection | null>(null);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState<'all' | 'machine'>('all');
  const [machineId, setMachineId] = useState('');
  const [selected, setSelected] = useState<RediscoverGame | null>(null);
  const cycles = useRef(new Map<string, string[]>());
  useEffect(() => {
    let active = true;
    getRediscoverCollection()
      .then((data) => {
        if (!active) return;
        setCollection(data);
        setMachineId(data.machines[0]?.id ?? '');
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, []);
  const machine = collection?.machines.find((item) => item.id === machineId);
  const games = !collection
    ? []
    : mode === 'all'
      ? eligibleGames(collection.games)
      : machine
        ? eligibleGames(collection.games, machine)
        : [];
  const draw = () => {
    const key = mode === 'all' ? 'all' : `machine:${machineId}`;
    const next = drawGame(games, cycles.current.get(key) ?? []);
    cycles.current.set(key, next.seen);
    setSelected(next.game);
  };
  const changeMode = (value: 'all' | 'machine') => {
    setMode(value);
    setSelected(null);
  };
  return (
    <div className="mobile-page rediscover-page">
      <Link className="back" to="/">
        ← Accueil
      </Link>
      <span className="mode">Votre collection · Un nouveau regard</span>
      <h1>
        <Dices aria-hidden="true" /> Redécouvrir ma collection
      </h1>
      <p className="lead">
        Choisissez un jeu au hasard parmi les jeux que vous possédez réellement.
      </p>
      {error ? (
        <p role="alert" className="error-state">
          Impossible de charger la collection. Revenez sur cette page pour réessayer.
        </p>
      ) : !collection ? (
        <p role="status">Chargement des jeux possédés…</p>
      ) : (
        <>
          <div className="rediscover-modes" role="group" aria-label="Mode de tirage">
            <div className={`rediscover-mode ${mode === 'all' ? 'selected' : ''}`}>
              <button type="button" aria-pressed={mode === 'all'} onClick={() => changeMode('all')}>
                <Gamepad2 aria-hidden="true" />
                <strong>Toute ma collection</strong>
                <span>Tirer un jeu au hasard parmi tous vos jeux possédés.</span>
                <small>{mode === 'all' ? '✓ Sélectionné' : 'Sélectionner'}</small>
              </button>
            </div>
            <div className={`rediscover-mode ${mode === 'machine' ? 'selected' : ''}`}>
              <button
                type="button"
                aria-pressed={mode === 'machine'}
                onClick={() => changeMode('machine')}
              >
                <Monitor aria-hidden="true" />
                <strong>Par machine</strong>
                <span>Tirer un jeu au hasard parmi les jeux d’une machine spécifique.</span>
                <small>{mode === 'machine' ? '✓ Sélectionné' : 'Sélectionner'}</small>
              </button>
              <label>
                Machine
                <select
                  value={machineId}
                  disabled={!collection.machines.length}
                  onChange={(event) => {
                    setMachineId(event.target.value);
                    changeMode('machine');
                  }}
                >
                  {!collection.machines.length && (
                    <option value="">Aucune machine disponible</option>
                  )}
                  {collection.machines.map((item) => (
                    <option value={item.id} key={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
          {!games.length ? (
            <p role="status" className="empty-state">
              {mode === 'all'
                ? 'Aucun jeu possédé n’est disponible pour le tirage.'
                : 'Aucun jeu possédé n’est disponible pour cette machine.'}
            </p>
          ) : (
            !selected && (
              <button className="rediscover-action" type="button" onClick={draw}>
                <Dices aria-hidden="true" /> Tirer un jeu au hasard
              </button>
            )
          )}
          <div aria-live="polite" aria-atomic="true">
            {selected && (
              <section className="rediscover-result" aria-label="Jeu tiré au hasard">
                <h2>Jeu tiré au hasard</h2>
                <div className="rediscover-game">
                  {renderCover(selected)}
                  <div>
                    <h3>{selected.title}</h3>
                    <p>{selected.platformName}</p>
                    {selected.releaseYear != null && <p>{selected.releaseYear}</p>}
                    {selected.category && <p>{selected.category}</p>}
                  </div>
                </div>
                <div className="rediscover-actions">
                  <Link
                    className="rediscover-action"
                    to={`/games/${encodeURIComponent(selected.id)}`}
                  >
                    Voir la fiche
                  </Link>
                  <button
                    className="rediscover-action rediscover-another"
                    type="button"
                    onClick={draw}
                  >
                    <Dices aria-hidden="true" /> Un autre jeu
                  </button>
                </div>
              </section>
            )}
          </div>
        </>
      )}
    </div>
  );
}
